import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPaymentOptionsValues } from '@acorn-portal/shared/src/pages/renewal/PaymentOptions';
import {
	CreateNewRenewalVersionResponse,
	RenewalRatingResponse,
	SelectedAddOn,
} from '@acorn-portal/shared/src/services/renewalsApi';

// Treated like an addon on front end but is a rateable object in ICE
export interface INoClaimsBonusAddon {
	ncbProtection: boolean;
}

export interface IAddonState {
	addons: SelectedAddOn[];
}

export interface IFutureRenewalState {
	isFutureDatedRenewal: boolean;
	futureRenewalStartDate: string | undefined;
}
export interface ICreateNewRenewalVersionResponse
	extends CreateNewRenewalVersionResponse,
		IAddonState,
		INoClaimsBonusAddon {
	isNewVersion: boolean;
	renewalStartDate: string;
	renewalRatingResponse: RenewalRatingResponse | undefined;
	isVehicleEligible: boolean;
	hasInitialData: boolean;
	paymentOptions: {
		method: string;
		deposit?: boolean;
	};
	checkout: {
		toPay?: number;
	};
	policyReference: string;
	isFutureDatedRenewal: boolean;
	futureRenewalStartDate: string | undefined;
	futureRenewalNewPolicyNo?: string;
	hasDeclined?: boolean;
}

const INITIAL_STATE: ICreateNewRenewalVersionResponse = {
	isNewVersion: false,
	isVehicleEligible: false,
	hasInitialData: false,
	newRenewalTransactionId: '',
	vehicleDetails: {
		abiCode: '',
		abiGroup: 0,
		annualMileage: 0,
		businessMileage: 0,
		bodyType: '',
		registration: '',
		make: '',
		model: '',
		numberOfDoors: '',
		fuelType: '',
		transmission: '',
		grossWeight: undefined,
		engineSize: 0,
		value: 0,
		yearOfManufacture: 0,
		isLeftHandDrive: false,
		datePurchased: '',
		isQPlate: false,
		isImported: false,
		vehicleUse: '',
		ownership: '',
		registeredKeeper: '',
		registrationDate: '',
		countryOfRegistration: '',
		numberOfSeats: '',
		vehicleOvernight: '',
		overnightAddress: undefined,
		modifications: [],
		securityDevices: undefined,
		coverDefinitionCode: undefined,
		licencingAuthority: '',
	},
	policyDetails: {
		policyholder: {
			isHomeOwner: false,
			address: {
				postcode: '',
				line1: '',
				city: '',
				country: '',
			},
		},
		operatingArea: '',
	},
	drivers: [],
	renewalStartDate: '',
	renewalRatingResponse: undefined,
	paymentOptions: {
		method: '',
		deposit: undefined,
	},
	checkout: {
		toPay: undefined,
	},
	policyReference: '',
	addons: [],
	isFutureDatedRenewal: false,
	futureRenewalStartDate: undefined,
	hasDeclined: false,
	ncbProtection: false,
};

const getTotalToPay = (method: string, state: ICreateNewRenewalVersionResponse) => {
	switch (method) {
		case 'total':
			return state.renewalRatingResponse?.annualPremium;
		case 'finance':
			return state.renewalRatingResponse?.financePlanWithDeposit?.depositAmount;
		default:
			return undefined;
	}
};

export const renewalSlice = createSlice({
	name: 'renewal',
	initialState: INITIAL_STATE,
	reducers: {
		updateFutureDatedRenewal: (state, action: PayloadAction<IFutureRenewalState>) => ({
			...state,
			isFutureDatedRenewal: action.payload.isFutureDatedRenewal,
			futureRenewalStartDate: action.payload.futureRenewalStartDate,
		}),

		setNewFutureDatedRenewalPolicyNumber: (state, action: PayloadAction<string>) => ({
			...state,
			futureRenewalNewPolicyNo: action.payload,
		}),

		clearRenewalState: () => ({
			...INITIAL_STATE,
		}),
		updateRenewalRate: (state, action: PayloadAction<RenewalRatingResponse>) => ({
			...state,
			renewalRatingResponse: action.payload,
			ncbProtection: action.payload.coverDetails?.ncbDetails?.isNCBSelected ?? state.ncbProtection,
			addons:
				action.payload.addOns
					?.filter((addon) => addon.selected)
					?.map((addon) => ({
						underwrittenDataPointId: addon.underwrittenDataPointId,
						addOnCode: addon.addOnCode,
					})) ?? state.addons,
		}),

		updateRenewalDetails: (state, action: PayloadAction<Partial<ICreateNewRenewalVersionResponse>>) => {
			const newState: ICreateNewRenewalVersionResponse = {
				...state,
				...action.payload,
				vehicleDetails: {
					...state.vehicleDetails,
					...action.payload.vehicleDetails,
					overnightAddress: action.payload.vehicleDetails?.overnightAddress || {
						line1: '',
						city: '',
						postcode: '',
					},
				},
			};
			return newState;
		},

		updateAddons: (state, action: PayloadAction<IAddonState>) => ({
			...state,
			...action.payload,
		}),

		updateCheckout: (state, action: PayloadAction<IPaymentOptionsValues>) => ({
			...state,
			checkout: {
				toPay: getTotalToPay(action.payload.method as string, state),
			},
		}),

		updatePaymentMethod: (state, action: PayloadAction<IPaymentOptionsValues>) => ({
			...state,
			paymentOptions: {
				...state.paymentOptions,
				...action.payload,
			},
			checkout: {
				toPay: getTotalToPay(action.payload.method as string, state),
			},
		}),
	},
});

export const {
	updateAddons,
	updateCheckout,
	updatePaymentMethod,
	updateRenewalDetails,
	updateRenewalRate,
	clearRenewalState,
	updateFutureDatedRenewal,
	setNewFutureDatedRenewalPolicyNumber,
} = renewalSlice.actions;

export default renewalSlice.reducer;
